/**
 * @fileoverview App-level Redux reducer for managing core application state.
 * 
 * This reducer manages:
 * - CSA (Credit Support Annex) data for all types (All, Monitoring, IM)
 * - Application initialization status
 * - End-of-day (EOD) date information
 * - SIMM version information
 * 
 * @module app/reducer
 */

import fp from 'lodash/fp';
import * as actions from './actions';

import { lastWorkingDay } from '../common/helpermethods/HelperMethods';

/**
 * Initial state for the app reducer.
 * 
 * @typedef {Object} AppState
 * @property {Array<Object>} csaData - All CSA data (all types)
 * @property {Array<Object>} monitoringCsaData - Monitoring CSAs only
 * @property {Array<Object>} imCsaData - IM (Initial Margin) CSAs only
 * @property {boolean} initialised - Has the app completed initialization?
 * @property {Object} EOD - End-of-day date information
 * @property {string} EOD.from - Start date for EOD range
 * @property {string} EOD.to - End date for EOD range
 * @property {boolean} EOD.today - Whether EOD is today's date
 * @property {string} simmVersion - SIMM version number (e.g., "2.6")
 */
export const INITIAL_STATE = {
    csaData: [],
    monitoringCsaData: [],
    imCsaData: [],
    initialised: false,
    EOD: { from: lastWorkingDay(), to: lastWorkingDay(), today: false },
    simmVersion: 'N/A'
};

/**
 * App reducer function.
 * 
 * @param {AppState} state - Current state (defaults to INITIAL_STATE)
 * @param {Object} action - Redux action object
 * @param {string} action.type - Action type
 * @returns {AppState} New state
 * 
 * @example
 * // Usage in store
 * const store = createStore(combineReducers({ app: appReducer }));
 * 
 * @example
 * // Dispatching an action
 * dispatch(fetchCsaDataSucceeded(csaList));
 * // State will be updated with csaData, monitoringCsaData, and imCsaData
 */
export default function reducer(state = INITIAL_STATE, action = {}) {
    switch (action.type) {
        case actions.SET_INITIALISED:
            return fp.set('initialised', action.initialised, state);
        case actions.FETCH_CSA_DATA_SUCCEEDED:
            return fp.flow(
                fp.set('csaData', fp.cloneDeep(action.csas)),
                fp.set('monitoringCsaData', fp.filter(csa => csa.type === 'Monitoring')(action.csas)),
                fp.set('imCsaData', fp.filter(csa => csa.type === 'IM')(action.csas))
            )(state);
        case actions.FETCH_MONITORING_CSA_DATA_SUCCEEDED:
            return fp.set('monitoringCsaData', fp.cloneDeep(action.csas))(state);
        case actions.FETCH_IM_CSA_DATA_SUCCEEDED:
            return fp.set('imCsaData', fp.cloneDeep(action.csas))(state);
        case actions.FETCH_EOD_SUCCEEDED:
            return fp.flow(
                fp.set('EOD.from', fp.cloneDeep(action.data)),
                fp.set('EOD.to', fp.cloneDeep(action.data))
            )(state);
        case actions.FETCH_SIMM_VERSION_SUCCEEDED:{
            fp.set('APP_ENV_CONFIG.REACT_APP_SIMM_VERSION', action.data, window)
            return fp.set('simmVersion', action.data, state);
        }
        default:
            return state;
    }
}
